import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { StatusBadge } from '../../components/admin/StatusBadge';

interface Contract {
  id: string;
  contractNumber: string;
  buyerReference: string;
  buyerId: string | null;
  buyerName: string | null;
  status: string;
  coffeeDescription: string;
  totalQuantityKg: string;
  totalValue: string;
  currency: string;
  incoterms: string;
  shippingMonth: string;
  createdAt: string;
}

export const ContractList: React.FC = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetch('/api/admin/export-invoices?resource=contracts', { credentials: 'include' })
      .then(r => r.json())
      .then(data => setContracts(Array.isArray(data) ? data : []))
      .catch(() => setContracts([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? contracts : contracts.filter(c => c.status === filter);

  const fmtCurrency = (amount: string, cur: string = 'USD') => {
    const symbols: Record<string, string> = { USD: '$', EUR: '\u20AC', GBP: '\u00A3', KES: 'KES ' };
    const sym = symbols[cur] || cur + ' ';
    return `${sym}${parseFloat(amount || '0').toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  const totalValue = contracts.filter(c => c.status === 'active').reduce((sum, c) => sum + parseFloat(c.totalValue || '0'), 0);
  const totalQty = contracts.filter(c => c.status === 'active').reduce((sum, c) => sum + parseFloat(c.totalQuantityKg || '0'), 0);

  const handleDelete = async (id: string, num: string) => {
    if (!confirm(`Delete contract ${num}?`)) return;
    const res = await fetch('/api/admin/export-invoices?resource=contracts', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ id }),
    });
    if (res.ok) setContracts(prev => prev.filter(c => c.id !== id));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">Contracts</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">{contracts.length} total contracts</p>
        </div>
        <Link to="/admin/contracts/new" className="px-5 py-2.5 bg-[#1A1A1A] text-white rounded-xl text-sm font-medium hover:bg-[#2a2a2a] active:scale-[0.98] transition-all shadow-sm shadow-black/10">
          + New Contract
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-5 mb-6">
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-5 shadow-sm shadow-black/[0.03]">
          <p className="text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Active Contracts</p>
          <p className="text-2xl font-bold text-[#1A1A1A] mt-1.5 tabular-nums">{contracts.filter(c => c.status === 'active').length}</p>
        </div>
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-5 shadow-sm shadow-black/[0.03]">
          <p className="text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Active Value</p>
          <p className="text-2xl font-bold text-[#1A1A1A] mt-1.5 tabular-nums">${totalValue.toLocaleString('en-US', { minimumFractionDigits: 0 })}</p>
        </div>
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-5 shadow-sm shadow-black/[0.03]">
          <p className="text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Active Quantity</p>
          <p className="text-2xl font-bold text-[#1A1A1A] mt-1.5 tabular-nums">{totalQty.toLocaleString()} kg</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-1 bg-[#F5F3F0] rounded-xl p-1 w-fit mb-6">
        {['all', 'active', 'fulfilled', 'cancelled'].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3.5 py-2 text-xs font-medium rounded-lg transition-all ${filter === s ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#6B6B6B] hover:text-[#1A1A1A]'}`}
          >
            {s === 'all' ? `All (${contracts.length})` : `${s.charAt(0).toUpperCase() + s.slice(1)} (${contracts.filter(c => c.status === s).length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-white rounded-2xl animate-pulse shadow-sm" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#A3A3A3]">No contracts found.</div>
      ) : (
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl overflow-hidden shadow-sm shadow-black/[0.03]">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E8E6E2]/60 bg-[#FAFAF8]/50">
                <th className="text-left px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Contract #</th>
                <th className="text-left px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Buyer</th>
                <th className="text-left px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Coffee</th>
                <th className="text-right px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Quantity</th>
                <th className="text-right px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Value</th>
                <th className="text-left px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Status</th>
                <th className="text-right px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} className="border-b border-[#E8E6E2]/60 hover:bg-[#FAFAF8]/50 transition-colors">
                  <td className="px-5 py-4">
                    <Link to={`/admin/contracts/${c.id}`} className="font-semibold text-[#1A1A1A] hover:text-[#F05A1E] transition-colors">
                      {c.contractNumber}
                    </Link>
                    {c.shippingMonth && <p className="text-xs text-[#A3A3A3] mt-0.5">{c.shippingMonth}</p>}
                  </td>
                  <td className="px-5 py-4 text-sm text-[#6B6B6B]">{c.buyerName || '-'}</td>
                  <td className="px-5 py-4 text-sm text-[#6B6B6B]">{c.coffeeDescription || '-'}</td>
                  <td className="px-5 py-4 text-sm text-[#1A1A1A] text-right tabular-nums">{parseFloat(c.totalQuantityKg || '0').toLocaleString()} kg</td>
                  <td className="px-5 py-4 text-sm font-semibold text-[#1A1A1A] text-right tabular-nums">{fmtCurrency(c.totalValue, c.currency)}</td>
                  <td className="px-5 py-4"><StatusBadge status={c.status} /></td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => navigate(`/admin/contracts/${c.id}`)} className="px-2.5 py-1.5 text-[12px] font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE8] rounded-lg transition-colors">Edit</button>
                      <button onClick={() => handleDelete(c.id, c.contractNumber)} className="px-2.5 py-1.5 text-[12px] font-medium text-[#C4C4C4] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
